/* Millionaire Strands – banner slideshow: smooth slide/fade, swipe, auto-play, clickable banners */
if (!customElements.get('ms-banner-slider')) {
  customElements.define(
    'ms-banner-slider',
    class MsBannerSlider extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.ms-bn__viewport');
        this.track = this.querySelector('.ms-bn__track');
        this.slides = Array.from(this.track.children);
        this.dots = Array.from(this.querySelectorAll('.ms-bn__dot'));
        this.count = this.slides.length;
        this.index = 0;
        this.fade = this.classList.contains('ms-bn--fade');
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.speed = Math.max(2, parseInt(this.dataset.speed, 10) || 4) * 1000;
        this.autoplay = this.dataset.autoplay === 'true';
        if (this.count < 2) return;

        // Seamless loop for the slide animation: a copy of banner 1 sits after the last banner.
        if (!this.fade) {
          this.clone = this.slides[0].cloneNode(true);
          this.clone.removeAttribute('id');
          this.clone.classList.remove('is-active');
          this.clone.setAttribute('aria-hidden', 'true');
          this.clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));
          this.track.appendChild(this.clone);
          this.track.addEventListener('transitionend', (e) => {
            if (e.target === this.track && this.pos === this.count) this.jump(0);
          });
        }
        this.pos = 0;

        this.querySelector('.ms-bn__arrow--prev')?.addEventListener('click', () => this.prev());
        this.querySelector('.ms-bn__arrow--next')?.addEventListener('click', () => this.next());
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

        this.bindSwipe();

        // pause while a computer mouse rests on the banner (phones never get stuck paused)
        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.addEventListener('pointerenter', (e) => { if (canHover && e.pointerType === 'mouse') { this.hovering = true; this.stop(); } });
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hovering = false; this.schedule(); } });
        this.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) this.stop(); });
        this.addEventListener('focusout', () => this.schedule());
        document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.schedule()));

        if ('IntersectionObserver' in window) {
          this.visible = true;
          new IntersectionObserver((entries) => {
            this.visible = entries[0].isIntersecting;
            this.visible ? this.schedule() : this.stop();
          }).observe(this);
        }

        // Warm up the other banners so every slide is ready before it appears.
        const warm = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        if (document.readyState === 'complete') setTimeout(warm, 300);
        else window.addEventListener('load', () => setTimeout(warm, 300), { once: true });

        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.indexOf(e.target.closest ? e.target.closest('.ms-bn__slide') : null);
          if (i > -1) { this.paused = true; this.stop(); this.go(i, true); }
        });
        document.addEventListener('shopify:block:deselect', () => { this.paused = false; this.schedule(); });

        this.schedule();
      }

      disconnectedCallback() { this.stop(); }

      width() { return this.viewport.clientWidth || 1; }

      setPos(pos, animate) {
        this.pos = pos;
        if (this.fade) return;
        this.track.classList.toggle('no-anim', !animate || this.reduce);
        this.track.style.transform = `translate3d(${-pos * 100}%, 0, 0)`;
      }

      jump(pos) {
        this.setPos(pos, false);
        void this.track.offsetWidth; // apply without animation
        this.track.classList.remove('no-anim');
      }

      go(i, instant) {
        const target = ((i % this.count) + this.count) % this.count;
        if (this.fade) {
          this.pos = target;
        } else if (i >= this.count && !instant) {
          this.setPos(this.count, true); // slide onto the copy of banner 1, then reset quietly
        } else {
          this.setPos(target, !instant);
        }
        this.index = target;
        this.slides.forEach((s, k) => {
          const on = k === target;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', on ? 'false' : 'true');
          s.querySelectorAll('a').forEach((a) => (on ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
        });
        this.dots.forEach((d, k) => {
          d.classList.toggle('is-active', k === target);
          d.toggleAttribute('aria-current', k === target);
        });
        this.schedule();
      }

      next() {
        if (!this.fade && this.pos === this.count) this.jump(0);
        this.go(this.index + 1);
      }

      prev() {
        if (!this.fade && this.index === 0) {
          this.jump(this.count); // start from the copy of banner 1 so it slides backwards smoothly
          this.go(this.count - 1);
          return;
        }
        this.go(this.index - 1);
      }

      schedule() {
        this.stop();
        if (!this.autoplay || this.paused || this.hovering || this.dragging || document.hidden || this.visible === false) return;
        this.timer = setTimeout(() => this.next(), this.speed);
      }

      stop() { clearTimeout(this.timer); }

      bindSwipe() {
        let startX = 0, startY = 0, dx = 0, t0 = 0, tracking = false;
        const vp = this.viewport;

        vp.addEventListener('pointerdown', (e) => {
          if (e.button !== 0) return;
          tracking = true; this.dragging = false; this.moved = false;
          startX = e.clientX; startY = e.clientY; dx = 0; t0 = performance.now();
          if (!this.fade && this.pos === this.count) this.jump(0);
        });

        vp.addEventListener('pointermove', (e) => {
          if (!tracking) return;
          dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (!this.dragging) {
            if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) {
              if (Math.abs(dy) > 10) tracking = false; // the shopper is scrolling the page
              return;
            }
            this.dragging = true; this.moved = true; this.stop();
            try { vp.setPointerCapture(e.pointerId); } catch (_) {}
            if (!this.fade) {
              // dragging back from banner 1: stand on its copy at the end so the last banner can slide in
              if (dx > 0 && this.pos === 0) this.jump(this.count);
              this.track.classList.add('is-dragging');
            }
          }
          if (this.fade) return;
          this.track.style.transform = `translate3d(calc(${-this.pos * 100}% + ${dx}px), 0, 0)`;
        });

        const end = () => {
          if (!tracking) return;
          tracking = false;
          if (!this.dragging) return;
          this.dragging = false;
          this.track.classList.remove('is-dragging');
          const fast = Math.abs(dx) / Math.max(1, performance.now() - t0) > 0.35;
          const far = Math.abs(dx) > this.width() * 0.18;
          if ((fast || far) && dx < 0) this.next();
          else if ((fast || far) && dx > 0) this.prev();
          else if (!this.fade && this.pos === this.count) { this.setPos(this.count, true); this.schedule(); }
          else this.go(this.index);
        };
        vp.addEventListener('pointerup', end);
        vp.addEventListener('pointercancel', end);
        vp.addEventListener('lostpointercapture', (e) => { if (e.target === vp) end(); });

        // A swipe should not also open the product.
        vp.addEventListener('click', (e) => {
          if (this.moved) { e.preventDefault(); e.stopPropagation(); this.moved = false; }
        }, true);
        vp.addEventListener('dragstart', (e) => e.preventDefault());
      }
    }
  );
}